import pageBuilderStore from "./pageBuilderStore";
import { 
	IBlockEditingState, 
	IBlockMgmt, 
	IDesignTreeEntry, 
	IHeroSlideEditing, 
	IDimensionField, 
	IFourSideField, 
	IFourCornersField 
} from "./state-interfaces";
import { IColumnsBlockData } from "../types/content-block-data";
import {IContentBlock, IDesignBlock} from "../types/iblock";
import contentManager from "./content-manager";
import { IImageAsset } from "../components/ui/shared/draggable-asset";


/**
 * @class BlockManager
 * @description
 * Centralized controller for all design/content block management logic in the PageBuilder application.
 *
 * Encapsulates all state and mutation logic related to block structure, design tree, and block operation history.
 * Exposes only explicit getter and setter methods—direct state access is fully prevented.
 *
 * ## Features:
 * - Maintains the master design tree for block hierarchy, order, and UI metadata.
 * - Provides robust getter/setter APIs for manipulating and querying block state.
 * - Prevents accidental direct mutation via internal `blockMgmtState` encapsulation.
 * - Handles add, remove, move, and reorder operations for all block types (including nested/column blocks).
 * - Synchronizes block references (`designBlockRef`, `contentBlockRef`) and mapping for DnD, editing, and preview.
 * - Prepares the foundation for undo/redo, operation history, and real-time collaboration features.
 * - Integrates with serialization/deserialization pipelines for page load and export.
 * - Designed for extensibility: new block types, custom UI logic, etc.
 *
 * ## Method Categories:
 * - **Getters**: Query block state, design tree entries, operation history, etc.
 * - **Setters**: Add, remove, move, update block data and metadata.
 * - **Helpers**: Sorting, mapping, and block ID/ref utilities.
 *
 * ## Usage Example:
 * ```ts
 * blockManager.addDesignBlock(block);
 * const tree = blockManager.getDesignTree();
 * blockManager.moveBlock(fromId, toId);
 * ```
 *
 * @file blocks-manager.ts
 * @since 2025-06-20
 * @author [Your Name]
 */

class BlocksManager {
  // ───────────────────────────────────────────────
  // 🎯 GETTER / SETTERS
  // ───────────────────────────────────────────────

  /** Gets the entire block management state object. */
  getBlockMgmtState(): IBlockMgmt {
    return pageBuilderStore.blockMgmtState;
  }

  /**
   * Sets the currently selected (editing) block and its type in the store.
   * Use `undefined` for both to clear selection.
   *
   * @param {string | undefined} blockId - The block's unique ID, or undefined to deselect.
   * @param {string | undefined} blockType - The block's type, or undefined to deselect.
   */
  setBlockEditing(blockId?: string, blockType?: string) {
    pageBuilderStore.blockMgmtState.blockEditing.selectedBlockId = blockId;
    pageBuilderStore.blockMgmtState.blockEditing.selectedBlockType = blockType;
  }

	/**
	 * Retrieves the currently selected image asset from the image library, if any.
	 * Useful for determining which image is active or selected in the context of editing or insertion.
	 *
	 * @returns {IImageAsset | undefined} The currently selected image asset, or undefined if none is selected.
	 *
	 * @example
	 * const image = blocksManager.getSelectedImage();
	 * if (image) {
	 *   // Do something with the selected image
	 * }
	 */
  getSelectedImage(): IImageAsset | undefined {
    return pageBuilderStore.blockMgmtState.selectedImage;
  }

	/**
	 * Sets the currently selected image asset in the image library.
	 * Can be used to highlight or prepare an image for insertion, editing, or preview.
	 *
	 * @param {IImageAsset | undefined} image - The image asset to set as selected, or undefined to clear the selection.
	 *
	 * @example
	 * // Set a specific image as selected
	 * blocksManager.setSelectedImage({ url: '/img/photo.jpg' });
	 *
	 * // Clear the selected image
	 * blocksManager.setSelectedImage(undefined);
	 */
  setSelectedImage(image: IImageAsset | undefined) {
    pageBuilderStore.blockMgmtState.selectedImage = image;
  }

	/**
	 * Returns whether the image gallery dialog is currently open.
	 * Useful for controlling or querying the visibility state of the image selection modal.
	 *
	 * @returns {boolean} True if the gallery dialog is open; otherwise, false.
	 *
	 * @example
	 * if (blocksManager.getOpenGalleryDialog()) {
	 *   // Render the modal or prevent duplicate openings
	 * }
	 */
  getOpenGalleryDialog(): boolean {
    return pageBuilderStore.blockMgmtState.openGalleryDialog;
  }

	/**
	 * Sets the open/close state of the image gallery dialog.
	 * Use this to show or hide the image selection modal in the UI.
	 *
	 * @param {boolean} value - True to open the gallery dialog; false to close it.
	 *
	 * @example
	 * // Open the gallery dialog
	 * blocksManager.setOpenGalleryDialog(true);
	 *
	 * // Close the gallery dialog
	 * blocksManager.setOpenGalleryDialog(false);
	 */
  setOpenGalleryDialog(value: boolean) {
    pageBuilderStore.blockMgmtState.openGalleryDialog = value;
  }

  /**
   * Returns the current block editing state (selected block ID and type).
   *
   * @returns {IBlockEditingState} The editing state object for the currently selected block.
   */
  getBlockEditing(): IBlockEditingState {
    return pageBuilderStore.blockMgmtState.blockEditing;
  }

	/**
	 * Retrieves the current editing state or configuration for the Hero Slider block.
	 * Typically includes details such as which slide is being edited and related options.
	 *
	 * @returns {IHeroSlideEditing} The current Hero Slider editing state object.
	 *
	 * @example
	 * const slideEditing = blocksManager.getHeroSlideEditing();
	 * // Access the active slide index: slideEditing.activeIndex
	 */
  getHeroSlideEditing(): IHeroSlideEditing {
    return pageBuilderStore.blockMgmtState.heroSlideEditing;
  }

	/**
	 * Sets the current editing state or configuration for the Hero Slider block.
	 * Updates which slide is being edited and related editing options.
	 *
	 * @param {IHeroSlideEditing} value - The new editing state object for the Hero Slider.
	 *
	 * @example
	 * // Set the active slide to index 2 for editing
	 * blocksManager.setHeroSlideEditing({ activeIndex: 2, ...otherFields });
	 */
  setHeroSlideEditing(value: IHeroSlideEditing) {
    pageBuilderStore.blockMgmtState.heroSlideEditing = value;
  }

  /** Sets the dragging state for any block. */
  setIsDraggingBlock(value: boolean) {
    this.getBlockMgmtState().isDraggingBlock = value;
  }

  /** Returns whether a block is currently being dragged. */
  getIsDraggingBlock(): boolean {
    return this.getBlockMgmtState().isDraggingBlock;
  }

  /** Sets the ID of the currently hovered drop zone (or null if none). */
  setHoveredDropZoneId(dropZoneId: string | null) {
    this.getBlockMgmtState().hoveredDropZoneId = dropZoneId;
  }

  /** Gets the ID of the currently hovered drop zone (or null). */
  getHoveredDropZoneId(): string | null {
    return this.getBlockMgmtState().hoveredDropZoneId;
  }

  /** Sets the design block ID being dragged from (or null if none). */
  setFromDesignBlockId(id: string | null) {
    this.getBlockMgmtState().fromDesignBlockId = id;
  }

  /** Gets the design block ID being dragged from (or null). */
  getFromDesignBlockId(): string | null {
    return this.getBlockMgmtState().fromDesignBlockId;
  }

  /** Sets the index of the column being dragged from (or null). */
  setDraggingFromColIndex(index: number | null) {
    this.getBlockMgmtState().draggingFromColIndex = index;
  }

  /** Gets the index of the column being dragged from (or null). */
  getDraggingFromColIndex(): number | null {
    return this.getBlockMgmtState().draggingFromColIndex;
  }

  /** Sets the ID of the currently dragged block (or null if none). */
  setDraggingBlock(id: string | null) {
    this.getBlockMgmtState().draggingBlockId = id;
  }

  /** Gets the ID of the currently dragged block (or null). */
  getDraggingBlockId(): string | null {
    return this.getBlockMgmtState().draggingBlockId;
  }

  /** Sets the currently hovered target block ID (or null if none). */
  setHoveredTarget(id: string | null) {
    this.getBlockMgmtState().hoveredTargetId = id;
  }

  /** Gets the currently hovered target block ID (or null). */
  getHoveredTargetId(): string | null {
    return this.getBlockMgmtState().hoveredTargetId;
  }

  /** Sets the dropHandled flag (true if a drop action was processed). */
  setDropHandled(value: boolean) {
    this.getBlockMgmtState().dropHandled = value;
  }

  /** Returns whether a drop action has been handled. */
  getDropHandled() {
    return this.getBlockMgmtState().dropHandled;
  }

  /**
   * Returns the current designTree map from blockMgmtState.
   *
   * @returns {Map<string, IDesignTreeEntry>}
   */
  getDesignTree(): Map<string, IDesignTreeEntry> {
    return this.getBlockMgmtState().designTree;
  }

  // ───────────────────────────────────────────────
  // ➕ ADD ENTRY
  // ───────────────────────────────────────────────

  /**
   * Adds a new entry to the designTree.
   *
   * @param entry - The new design tree metadata to register
   */
  addDesignTreeEntry(entry: IDesignTreeEntry): void {
    this.getBlockMgmtState().designTree.set(entry.contentBlockId, entry);
  }

  // ───────────────────────────────────────────────
  // 🔄 UPDATE ENTRY
  // ───────────────────────────────────────────────

  /**
   * Updates an existing entry in the designTree by patching fields.
   *
   * @param contentBlockId - The block's unique contentBlockId
   * @param updates - Partial entry to merge
   */
  updateDesignTreeEntry(
    contentBlockId: string,
    updates: Partial<IDesignTreeEntry>
  ): void {
    const existing = this.getBlockMgmtState().designTree.get(contentBlockId);
    if (!existing) return;

    const updated = { ...existing, ...updates };
    this.getBlockMgmtState().designTree.set(contentBlockId, updated);
  }

  // ───────────────────────────────────────────────
  // ❌ REMOVE ENTRY
  // ───────────────────────────────────────────────

  /**
   * Removes an entry from the designTree.
   *
   * @param contentBlockId - The block's unique contentBlockId
   */
  removeDesignTreeEntry(contentBlockId: string): void {
    this.getBlockMgmtState().designTree.delete(contentBlockId);
  }

  /**
   * Re-sorts the `designTree` map to reflect the current structure and visual order
   * of all blocks in the page, including both canvas-level blocks and nested column blocks.
   *
   * This ensures that the internal `designTree` maintains a reliable sequence that
   * matches the layout rendered in the editor, which is critical for:
   *  - Preserving block order consistency during re-renders
   *  - Tracking column ownership via `colIndex`
   *  - Supporting operations like undo, serialization, or traversal
   *
   * Note: The sort order always inserts parent blocks (e.g. ColumnsBlock) before their
   * children (e.g. blocks inside ColumnCells).
   */
  sortDesignTreeRefs(): void {
    const newTree = new Map<string, IDesignTreeEntry>();
    const blockMgmtState: IBlockMgmt = this.getBlockMgmtState();

    function processBlock(
      block: IContentBlock,
      parentColIndex: number | null = null
    ) {
      // 🧠 Always process the parent block FIRST
      const entry = blockMgmtState.designTree.get(block.contentBlockId);
      if (entry) {
        if (parentColIndex !== null) entry.colIndex = parentColIndex;
        newTree.set(block.contentBlockId, entry);
      }

      // Then process children if it's a ColumnsBlock
      if (block.blockType === "columns") {
        if (Array.isArray((block.blockData as IColumnsBlockData).children)) {
          (block.blockData as IColumnsBlockData).children.forEach(
            (column: IDesignBlock[], colIndex: number) => {
              column.forEach((childBlock: IDesignBlock) => {
                processBlock(childBlock.contentBlock, colIndex);
              });
            }
          );
        }
      }
    }

    contentManager.getDesignContent().forEach((block: IDesignBlock) => {
      processBlock(block.contentBlock);
    });

    blockMgmtState.designTree = newTree;

    this.debugDesignTree("After sorting...");

    console.log("✅ designTree order synchronized (parent-before-child).");
  }

  /**
   * Logs the current state of the designTree to the console for debugging.
   *
   * @param label Optional label to group the log output
   */
  debugDesignTree(label: string = "🧩 Debug: designTree") {
    const designTree = this.getBlockMgmtState().designTree;

    console.groupCollapsed(label);
    designTree.forEach((entry, contentBlockId) => {
      console.log({
        contentBlockType: "[" + entry.contentBlockType + "]",
        contentBlockId,
        designBlockId: entry.designBlockId,
        location: entry.location,
        colIndex: entry.colIndex ?? null,
        designBlockRef: entry.designBlockRef?.current,
        contentBlockRef: entry.contentBlockRef?.current,
      });
    });
    console.groupEnd();
  }

  /**
   * Converts a dimension object (single, four-side, or four-corner) into a valid CSS value string.
   *
   * - If given a standard dimension ({ value, unit }), returns 'valueunit' (e.g., '2rem', '16px').
   * - If given an IFourSideField ({ top, right, bottom, left, unit, linked }), outputs a CSS shorthand string:
   *   - If all sides are equal or 'linked', returns a single value (e.g., '8px').
   *   - If sides differ, returns 'top right bottom left' shorthand (e.g., '8px 12px 8px 12px').
   * - If given an IFourCornersField ({ topLeft, topRight, bottomRight, bottomLeft, unit, linked }):
   *   - If all corners are equal or 'linked', returns a single value (e.g., '4px').
   *   - If corners differ, returns 'topLeft topRight bottomRight bottomLeft' shorthand (e.g., '4px 8px 12px 8px').
   * - For zero values, always returns "0" with no unit (per CSS best practices).
   * - Returns an empty string for unset or invalid inputs.
   *
   * @param dim   Dimension ({ value, unit }), IFourSideField (padding, margin, etc.), or IFourCornersField (border radius, etc.).
   * @returns     CSS value string (e.g., "2rem", "0", "8px 12px 8px 12px", "4px 8px 12px 8px"), or '' if invalid.
   *
   * @example
   *   dimensionToCss({ value: "2", unit: "rem" }) // "2rem"
   *   dimensionToCss({ top: "8", right: "8", bottom: "8", left: "8", unit: "px", linked: true }) // "8px"
   *   dimensionToCss({ top: "8", right: "12", bottom: "8", left: "12", unit: "px", linked: false }) // "8px 12px 8px 12px"
   *   dimensionToCss({ topLeft: "4", topRight: "8", bottomRight: "12", bottomLeft: "8", unit: "px", linked: false }) // "4px 8px 12px 8px"
   *   dimensionToCss({ topLeft: "5", topRight: "5", bottomRight: "5", bottomLeft: "5", unit: "px", linked: true }) // "5px"
   */
  dimensionToCss(
    dim?: { value?: string; unit?: string } | IFourSideField | IFourCornersField
  ): string {
    if (!dim) return "";

    // Existing four-side type guard
    const isFourSide = (d: any): d is IFourSideField =>
      typeof d.top === "string" &&
      typeof d.right === "string" &&
      typeof d.bottom === "string" &&
      typeof d.left === "string";

    // NEW: Four-corners type guard
    const isFourCorners = (d: any): d is IFourCornersField =>
      typeof d.topLeft === "string" &&
      typeof d.topRight === "string" &&
      typeof d.bottomRight === "string" &&
      typeof d.bottomLeft === "string";

    if (isFourSide(dim)) {
      // ...[your existing logic]...
      const { top, right, bottom, left, unit, linked } = dim;
      if (linked || (top === right && top === bottom && top === left)) {
        if (top === "0") return "0";
        return `${top}${unit || "px"}`;
      }
      const values = [top, right, bottom, left].map((v) =>
        v === "0" ? "0" : `${v}${unit || "px"}`
      );
      return values.join(" ");
    }
    // Add block for IFourCornersField
    else if (isFourCorners(dim)) {
      const { topLeft, topRight, bottomRight, bottomLeft, unit, linked } = dim;
      // If all corners are the same or linked, return just one value
      if (
        linked ||
        (topLeft === topRight &&
          topLeft === bottomRight &&
          topLeft === bottomLeft)
      ) {
        if (topLeft === "0") return "0";
        return `${topLeft}${unit || "px"}`;
      }
      // Otherwise, output CSS shorthand for four corners
      const values = [topLeft, topRight, bottomRight, bottomLeft].map((v) =>
        v === "0" ? "0" : `${v}${unit || "px"}`
      );
      return values.join(" ");
    }
    // Fallback: value/unit object
    else {
      const value = (dim as any).value;
      if (value === undefined || value === null || value === "") return "";
      if (value === "0") return "0";
      return (dim as any).unit ? `${value}${(dim as any).unit}` : `${value}`;
    }
  }

	/**
 * Parses a CSS dimension string (single, four-side, or four-corner) into a strongly typed field.
 *
 * - "single":     "8px"  → { value: "8", unit: "px" }
 * - "four-side":  "8px 12px 8px 12px" → { top, right, bottom, left, unit: "px", linked }
 * - "four-corners": "4px 8px 12px 8px" → { topLeft, topRight, bottomRight, bottomLeft, unit: "px", linked }
 *
 * Always defaults missing/invalid/zero values to "0" and unit to "px".
 */
parseDimensions(
  value: string | undefined,
  type: "single" | "four-side" | "four-corners"
): IDimensionField | IFourSideField | IFourCornersField {
  // Helper to parse a value/unit pair (e.g., "8px" or "2rem")
  const parseValueUnit = (str: string | undefined) => {
    if (!str || str === "0" || str.trim() === "") return { value: "0", unit: "px" };
    const match = /^(-?\d*\.?\d+)([a-z%]*)$/i.exec(str.trim());
    return match
      ? { value: match[1], unit: match[2] || "px" }
      : { value: "0", unit: "px" };
  };

  // Handle empty or "0" input (return all-zero for type)
  if (!value || value.trim() === "" || value.trim() === "0") {
    if (type === "single") return { value: "0", unit: "px" };
    if (type === "four-side") return { top: "0", right: "0", bottom: "0", left: "0", unit: "px", linked: true };
    if (type === "four-corners") return { topLeft: "0", topRight: "0", bottomRight: "0", bottomLeft: "0", unit: "px", linked: true };
  }

  // Split by spaces (for four-side/four-corner)
  const parts = (value ?? "").trim().split(/\s+/);

  if (type === "single") {
    const { value: v, unit } = parseValueUnit(parts[0]);
    return { value: v, unit };
  }

  if (type === "four-side") {
    // CSS shorthand: 1 = all, 2 = tb/rl, 3 = t/rl/b, 4 = t/r/b/l
    const [topV, rightV, bottomV, leftV] =
      parts.length === 1
        ? [parts[0], parts[0], parts[0], parts[0]]
        : parts.length === 2
        ? [parts[0], parts[1], parts[0], parts[1]]
        : parts.length === 3
        ? [parts[0], parts[1], parts[2], parts[1]]
        : [parts[0], parts[1], parts[2], parts[3]];

    const top = parseValueUnit(topV).value;
    const right = parseValueUnit(rightV).value;
    const bottom = parseValueUnit(bottomV).value;
    const left = parseValueUnit(leftV).value;
    // Assume unit is consistent (take from first, else default to "px")
    const unit = parseValueUnit(topV).unit || "px";
    const linked = top === right && top === bottom && top === left;
    return { top, right, bottom, left, unit, linked };
  }

  if (type === "four-corners") {
    // Always expect 4 values
    const [topLeftV, topRightV, bottomRightV, bottomLeftV] =
      parts.length === 1
        ? [parts[0], parts[0], parts[0], parts[0]]
        : [parts[0], parts[1] ?? parts[0], parts[2] ?? parts[0], parts[3] ?? parts[0]];

    const topLeft = parseValueUnit(topLeftV).value;
    const topRight = parseValueUnit(topRightV).value;
    const bottomRight = parseValueUnit(bottomRightV).value;
    const bottomLeft = parseValueUnit(bottomLeftV).value;
    const unit = parseValueUnit(topLeftV).unit || "px";
    const linked = topLeft === topRight && topLeft === bottomRight && topLeft === bottomLeft;
    return { topLeft, topRight, bottomRight, bottomLeft, unit, linked };
  }

  // Fallback (should not occur)
  return { value: "0", unit: "px" };
}
}

const blocksManager = new BlocksManager();
export default blocksManager;
